
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowUp } from 'lucide-react';
import './SearchForm.css';
import { toast } from 'react-toastify';

import {
    SearchHeader,
    SearchSubHeader,
    FilterSidebar,
    ResultModeSelector,
    DateCarousel,
    SortCards,
    SearchStatus,
    SearchLoader,
    ResultCardSkeleton
} from '../components/search-form';
import TravelResultsList from '../components/TravelResults/TravelResultsList';
import { useTravelWebSocket } from '../hooks/useTravelWebSocket';
import { buildTravelPayload } from '../utils/buildTravelPayload';
import { useSelector, useDispatch } from 'react-redux';
import { getUserIdentifier } from '../utils/identity';
import {
    selectTrainRoutes,
    selectFlightRoutes,
    selectMixedRoutes,
    selectAllRoutes,
    selectBusRoutes,
    setLoading as setReduxLoading,
    setHeaderLoading,
    clearRoutes
} from '../store/slices/travelSlice';
import AirportJsonConverter from '../components/search-form/convert';
import FallbackErrorModal from '../components/modal/FallbackErrorModal';

// Local Data for smart resolution
import stationsData from '../data/stations.json';
import airportsData from '../data/airports.json';

const SearchForm = () => {
    const dispatch = useDispatch();

    // Clear old data immediately on mount to prevent seeing old results when coming from Home
    useEffect(() => {
        dispatch(clearRoutes());
        dispatch(setReduxLoading(false));
        dispatch(setHeaderLoading(false));
    }, [dispatch]);

    const { status } = useParams();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const initialState = location.state || {};

    // Helper to get array from search params
    const getParamArray = (key, defaultVal) => {
        const val = searchParams.get(key);
        return val ? val.split(',') : defaultVal;
    };

    // Helper to get number from search params
    const getParamNum = (key, defaultVal) => {
        const val = searchParams.get(key);
        return val !== null ? Number(val) : defaultVal;
    };

    // Search States
    const [origin, setOrigin] = useState(searchParams.get('from') || initialState.fromLocation || '');
    const [destination, setDestination] = useState(searchParams.get('to') || initialState.toLocation || '');

    const [travelDate, setTravelDate] = useState(() => {
        const validateDate = (dateStr) => {
            const parsedDate = new Date(dateStr);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (parsedDate < today) {
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                return tomorrow;
            }
            return parsedDate;
        };

        const urlDate = searchParams.get('date');
        if (urlDate) return validateDate(urlDate);

        if (initialState.travelDate) return validateDate(initialState.travelDate);

        return new Date();
    });

    const [travelerCounts, setTravelerCounts] = useState(() => {
        const counts = initialState.passengerCounts || {};
        return {
            adults: getParamNum('adults', Number(counts.adults || counts.Adults || 1)),
            children: getParamNum('children', Number(counts.children || counts.Children || 0)),
            infants: getParamNum('infants', Number(counts.infants || counts.Infants || counts["Infants on lap"] || 0))
        };
    });

    const [selectedSeatClasses, setSelectedSeatClasses] = useState(() => {
        const urlClasses = getParamArray('seatClasses', null);
        if (urlClasses) return urlClasses;
        const classes = initialState.seatClasses;
        if (Array.isArray(classes) && classes.length > 0) return classes;
        return ['Economy', '3A'];
    });

    // Store full location objects (Initialize from state or URL params)
    const [originData, setOriginData] = useState(() => {
        if (initialState.fromLocationData) return initialState.fromLocationData;
        const slat = searchParams.get('slat');
        const slng = searchParams.get('slng');
        const stype = searchParams.get('stype');
        const scode = searchParams.get('scode');

        if (slat && slng) {
            return {
                lat: Number(slat),
                lng: Number(slng),
                label: searchParams.get('from'),
                code: scode || "",
                type: stype || ""
            };
        } else if (scode) {
            return {
                code: scode,
                type: stype || "",
                label: searchParams.get('from') || ""
            };
        }
        return null;
    });

    const [destinationData, setDestinationData] = useState(() => {
        if (initialState.toLocationData) return initialState.toLocationData;
        const dlat = searchParams.get('dlat');
        const dlng = searchParams.get('dlng');
        const dtype = searchParams.get('dtype');
        const dcode = searchParams.get('dcode');

        if (dlat && dlng) {
            return {
                lat: Number(dlat),
                lng: Number(dlng),
                label: searchParams.get('to'),
                code: dcode || "",
                type: dtype || ""
            };
        } else if (dcode) {
            return {
                code: dcode,
                type: dtype || "",
                label: searchParams.get('to') || ""
            };
        }
        return null;
    });

    // Filter States
    const [selectedModes, setSelectedModes] = useState(() => {
        const urlModes = getParamArray('modes', null);
        if (urlModes) return urlModes;
        if (initialState.activeModes) return initialState.activeModes;
        if (initialState.activeMode && initialState.activeMode !== 'All') return [initialState.activeMode];
        return ['All'];
    });

    // Track modes actually used for current results (only updates on Search click)
    const [appliedModes, setAppliedModes] = useState(selectedModes);

    const [resultsMode, setResultsMode] = useState(() => {
        const modes = (searchParams.get('modes') || '').split(',').filter(Boolean);
        // If multiple modes are present (tabs will be shown), default to 'All'
        if (modes.length > 1 || modes.includes('All')) return 'All';
        if (modes.length === 1) return modes[0];
        if (initialState.activeModes && initialState.activeModes.length > 1) return 'All';
        if (initialState.activeModes && initialState.activeModes.length === 1) return initialState.activeModes[0];
        return initialState.activeMode || 'All';
    });

    const [selectedSort, setSelectedSort] = useState('Best');
    const [nonStopOnly, setNonStopOnly] = useState(() => {
        const urlNonstop = searchParams.get('nonstop');
        if (urlNonstop !== null) return urlNonstop === '1';
        return initialState.nonStopOnly !== undefined ? initialState.nonStopOnly : false;
    });

    // Filter States
    // Initialize stopsFilter based on the initial nonStopOnly state
    const [stopsFilter, setStopsFilter] = useState({});




    const [operatorsFilter, setOperatorsFilter] = useState({});
    const [sourcesFilter, setSourcesFilter] = useState({});
    const [destinationsFilter, setDestinationsFilter] = useState({});
    const [viaFilter, setViaFilter] = useState({});
    const [operatorPrices, setOperatorPrices] = useState({});
    const [sourceKms, setSourceKms] = useState({});
    const [destinationKms, setDestinationKms] = useState({});
    // Default duration max (0h) and fare max (0) - dynamic based on data
    const [durationFilter, setDurationFilter] = useState({ min: 0, max: 0 }); // Hours
    const [fareFilter, setFareFilter] = useState({ min: 0, max: 0 }); // Price
    const [hideCancelled, setHideCancelled] = useState(false);
    const [showFullyAvailable, setShowFullyAvailable] = useState(false);
    const [avoidStationChange, setAvoidStationChange] = useState(false);
    const [showMobileFilters, setShowMobileFilters] = useState(false); // New state for mobile filters
    const [maxDurationLimit, setMaxDurationLimit] = useState(0);
    const [maxFareLimit, setMaxFareLimit] = useState(0);
    const [departureTimeFilter, setDepartureTimeFilter] = useState({ min: 0, max: 1440 });
    const [maxDepartureLimit, setMaxDepartureLimit] = useState(0);

    // Debounce Empty State to avoid flickering when data streams in
    const [isWaitingForEmpty, setIsWaitingForEmpty] = useState(false);

    // Scroll to Top state
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Calendar Fares State
    const [calendarFares, setCalendarFares] = useState({});
    const [isCalendarLoading, setIsCalendarLoading] = useState(false);

    // Network Connectivity State
    const [isOnline, setIsOnline] = useState(navigator.onLine);
    const [activeSearchMetadata, setActiveSearchMetadata] = useState(null);

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // WebSocket Hook
    const {
        sendMessage,
        connectionStatus,
        receivedRoutes,
        searchMetadata,
        loading: isLoading,
        headerLoading,
        error,
        closeConnection
    } = useTravelWebSocket();

    useEffect(() => {
        if (searchMetadata) {
            setActiveSearchMetadata(searchMetadata);
        }
    }, [searchMetadata]);

    // Identify specific backend error for full-screen handling - MUST BE AFTER useTravelWebSocket
    const isFlightModelError = typeof error === 'string' && error.includes('flight_model');
    const isTimeoutError = error === 'timeout';
    const shouldShowFullScreenStatus = !!status || isFlightModelError || isTimeoutError;

    const trainRoutes = useSelector(selectTrainRoutes);
    const flightRoutes = useSelector(selectFlightRoutes);
    const mixedRoutes = useSelector(selectMixedRoutes);
    const allRoutes = useSelector(selectAllRoutes);
    const busRoutes = useSelector(selectBusRoutes);

    const [globalFallbackError, setGlobalFallbackError] = useState(null);

    // Date Carousel Data
    const [dates, setDates] = useState([]);
    const dateItemsRef = useRef(null);
    const initialSearchTriggered = useRef(false);
    // viewDate controls the carousel window (arrow nav), travelDate is the actual selected/search date
    const [viewDate, setViewDate] = useState(initialState.travelDate ? new Date(initialState.travelDate) : new Date());

    // Sync viewDate (carousel focus) with travelDate (actual selected date)
    useEffect(() => {
        if (travelDate) {
            setViewDate(new Date(travelDate));
        }
    }, [travelDate]);

    // City Coordinates Fallback
    const cityCoordinates = {
        'Chennai': [13.0827, 80.2707],
        'Pune': [18.5204, 73.8567],
        'Mumbai': [19.0760, 72.8777],
        'Delhi': [28.6139, 77.2090],
        'Bangalore': [12.9716, 77.5946],
        'Hyderabad': [17.3850, 78.4867],
        'Kolkata': [22.5726, 88.3639],
        'Agra': [27.1767, 78.0081],
        'Jaipur': [26.9124, 75.7873],
    };

    const getCoordinates = (name, data) => {
        if (data && data.lat && data.lng) return [data.lat, data.lng];
        // If data has code but no lat/lng, we might need a lookup or API. 
        // For now, fallback to hardcoded or 0,0
        return cityCoordinates[name] || [0, 0];
    };

    // Helper to resolve coordinates (handles Google Place IDs and Smart Lookup Fallbacks)
    const resolveCoordinates = async (data, name) => {
        // 1. If we have lat/lng already (Static Data from selection), return it
        if (data && data.lat && data.lng) {
            return {
                code: data.code || "",
                lat: data.lat,
                lng: data.lng,
                name: data.label || name
            };
        }

        // 2. If we have a Place ID (Google Data), fetch details
        if (data && data.place_id && window.google && window.google.maps) {
            try {
                const geocoder = new window.google.maps.Geocoder();
                const result = await new Promise((resolve, reject) => {
                    geocoder.geocode({ placeId: data.place_id }, (results, status) => {
                        if (status === 'OK' && results[0]) {
                            resolve(results[0]);
                        } else {
                            reject(status);
                        }
                    });
                });

                const lat = result.geometry.location.lat();
                const lng = result.geometry.location.lng();
                return {
                    code: "", // No code for google places
                    lat: lat,
                    lng: lng,
                    name: data.label || name
                };
            } catch (err) {
                console.error(`Geocoding failed for ${name}:`, err);
            }
        }

        // 3. SMART LOOKUP: If data is missing but user typed a valid Code or Name
        // This handles cases like typing 'CSTM' and clicking search without selecting.
        // It also handles strings like "BHUSAVAL JN (BSL)" from URL params after refresh.
        let searchStr = (name || "").trim().toUpperCase();
        const scode = data?.code?.toUpperCase();

        // Check if the name contains a code in brackets (e.g. "BHUSAVAL JN (BSL)")
        const bracketMatch = searchStr.match(/\(([^)]+)\)$/);
        const bracketCode = bracketMatch ? bracketMatch[1].trim() : null;

        if (scode || bracketCode || searchStr.length >= 2) {
            // Priority 1: Exact Code Match (Fastest and most accurate)
            const targetCode = scode || bracketCode || searchStr;
            const codeMatch = stationsData.find(s => s.stationCode === targetCode) ||
                airportsData.find(a => (a.airportCode === targetCode || a.airportCode === searchStr));

            if (codeMatch) {
                return {
                    code: codeMatch.stationCode || codeMatch.airportCode,
                    lat: codeMatch.lat || (codeMatch.lat_long?.[0] ? Number(codeMatch.lat_long[0]) : 0),
                    lng: codeMatch.lng || (codeMatch.lat_long?.[1] ? Number(codeMatch.lat_long[1]) : 0),
                    name: codeMatch.stationName || codeMatch.airportName || name
                };
            }

            // Priority 2: Partial Name Match (Fallback for typed names like "Mumbai")
            const baseName = bracketCode ? searchStr.replace(`(${bracketCode})`, "").trim() : searchStr;
            const nameMatch = stationsData.find(s => (s.stationName || "").toUpperCase().startsWith(baseName)) ||
                airportsData.find(a => (a.airportName || "").toUpperCase().startsWith(baseName));

            if (nameMatch) {
                return {
                    code: nameMatch.stationCode || nameMatch.airportCode,
                    lat: nameMatch.lat || (nameMatch.lat_long?.[0] ? Number(nameMatch.lat_long[0]) : 0),
                    lng: nameMatch.lng || (nameMatch.lat_long?.[1] ? Number(nameMatch.lat_long[1]) : 0),
                    name: nameMatch.stationName || nameMatch.airportName || name
                };
            }
        }

        // 4. Fallback to City Name Lookup (Hardcoded list for legacy/common cities)
        const coords = cityCoordinates[name] || [0, 0];
        return {
            code: data?.code || "",
            lat: coords[0],
            lng: coords[1],
            name: name
        };
    };

    // Airport ya Station select hone par naam ke end mein suffix jodo
    // (agar pehle se "airport" / "station" word naam mein nahi hai)
    const appendAirportSuffix = (name, locationData) => {
        if (!name) return name;
        if (locationData?.type === 'airport' && !name.toLowerCase().includes('airport')) {
            return name + "‎ " + 'Airport';
        }
        if (locationData?.type === 'station' && !name.toLowerCase().includes('station')) {
            return name + "‎ " + 'Railway Station';
        }
        return name;
    };

    const handleSearch = async (overrides = {}, options = {}) => {
        if (connectionStatus === 'connecting') {
            return;
        }

        const { skipClear = false } = options;

        // Resolve the effective values: use override if provided, else current state
        const effectiveNonStop = overrides.nonStop !== undefined ? overrides.nonStop : nonStopOnly;
        const effectiveModes = overrides.modes || selectedModes;
        const effectiveDate = overrides.date || travelDate;



        // Immediately clear Redux store and show loading state BEFORE async geocoding.
        // This prevents old results from being visible while coordinates are being resolved.
        if (!skipClear) {
            dispatch(clearRoutes());
            dispatch(setReduxLoading(true));
            dispatch(setHeaderLoading(true));
        }

        // Resolve Coordinates (Async)
        const srcDetails = await resolveCoordinates(originData, origin);
        const destDetails = await resolveCoordinates(destinationData, destination);

        // ROBUST VALIDATION: Stop search if locations are completely unresolved
        const srcInvalid = !srcDetails.code && (!srcDetails.lat || srcDetails.lat === 0);
        const destInvalid = !destDetails.code && (!destDetails.lat || destDetails.lat === 0);

        if (srcInvalid || destInvalid) {
            console.warn('Search blocked: Unresolved location coordinates/codes.', { srcDetails, destDetails });

            if (srcInvalid) {
                toast.error(`"${origin || 'Origin'}" is not a recognized station or airport. Please select from the list.`, { position: 'top-center' });
            } else if (destInvalid) {
                toast.error(`"${destination || 'Destination'}" is not a recognized station or airport. Please select from the list.`, { position: 'top-center' });
            }

            dispatch(setReduxLoading(false));
            dispatch(setHeaderLoading(false));
            return;
        }

        // Safely store the metadata used for this specific search
        setActiveSearchMetadata({
            user_src_ll: [srcDetails.lat, srcDetails.lng],
            user_dest_ll: [destDetails.lat, destDetails.lng],
            user_src_name: origin,
            user_dest_name: destination
        });

        // Update URL params: ONLY include coordinates if we don't have a station/airport code (JSON)
        // Google selections won't have a code, so they MUST have a lat/lng for persistence.
        const paramsObj = {
            from: origin,
            scode: srcDetails.code || '',
            stype: originData?.type || '',
            to: destination,
            dcode: destDetails.code || '',
            dtype: destinationData?.type || '',
            date: effectiveDate ? (() => {
                const d = new Date(effectiveDate);
                const year = d.getFullYear();
                const month = String(d.getMonth() + 1).padStart(2, '0');
                const day = String(d.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            })() : '',
            adults: travelerCounts.adults,
            children: travelerCounts.children,
            infants: travelerCounts.infants,
            seatClasses: selectedSeatClasses.join(','),
            modes: effectiveModes.join(','),
            nonstop: effectiveNonStop ? '1' : '0'
        };

        // Add coordinates ONLY for Google/Generic selections (when no code is available)
        if (!srcDetails.code) {
            paramsObj.slat = srcDetails.lat;
            paramsObj.slng = srcDetails.lng;
        }
        if (!destDetails.code) {
            paramsObj.dlat = destDetails.lat;
            paramsObj.dlng = destDetails.lng;
        }

        const newParams = new URLSearchParams(paramsObj);
        setSearchParams(newParams, { replace: true });

        const srcPayload = {
            code: srcDetails.code,
            lat: srcDetails.lat,
            lng: srcDetails.lng
        };
        const destPayload = {
            code: destDetails.code,
            lat: destDetails.lat,
            lng: destDetails.lng
        };

        // Determine modes to send
        const modesToSend = effectiveModes;

        // Sync results tab and reset filters on fresh search
        if (!skipClear && modesToSend.length > 0) {
            setAppliedModes(modesToSend); // Update tabs to match current search
            // If multiple modes are selected (tabs will be shown), default to 'All'
            const newResultsMode = (modesToSend.includes('All') || modesToSend.length > 1) ? 'All' : modesToSend[0];
            setResultsMode(newResultsMode);
            // Reset main filters for the fresh data
            setDurationFilter({ min: 0, max: 999 });
            setFareFilter({ min: 0, max: 999999 });
            setDepartureTimeFilter({ min: 0, max: 1440 });
            // Respect effectiveNonStop: only show non-stop if active
            setStopsFilter({});
            setOperatorsFilter({});
            setSourcesFilter({});
            setDestinationsFilter({});
            setViaFilter({});
        }


        // console.log('DEBUG: handleSearch called with:', { overrides, options });
        // console.log('DEBUG: effectiveNonStop:', effectiveNonStop);
        const userIdentifier = getUserIdentifier();
        const fromFieldValue = `web_form_search:${userIdentifier}`;
        // Unique search session id for calendar fares and tracking
        // Generates exactly 8 character alphanumeric string (e.g., v5qn1nzb)
        const sessionId = Math.random().toString(36).substring(2, 10).padEnd(8, '0');

        // Parallel API call for calendar fares if Flight or All is in modes
        if (!skipClear && (modesToSend.includes('Flight') || modesToSend.includes('All'))) {
            setCalendarFares({}); // Clear old fares immediately
            setIsCalendarLoading(true); // Start loading state for calendar fares

            // AbortController to handle a 15-second timeout
            const controller = new AbortController();
            const signal = controller.signal;
            const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 seconds limit


            // Making the API call instantly alongside the WebSocket
            fetch(`https://hh183pxn0f.execute-api.ap-south-1.amazonaws.com/development/search?api_key=sh2818&search_session_id=${sessionId}&fetch_calendar_fares_from_ssid=1`, {
                signal: signal
            })
                .then(res => res.json())
                .then(data => {
                    clearTimeout(timeoutId); // Request succeeded, clear the abort timer
                    if (data?.data?.fares) {
                        setCalendarFares(data.data.fares);
                    }
                    setIsCalendarLoading(false);
                })
                .catch(err => {
                    setIsCalendarLoading(false);
                    if (err.name === 'AbortError') {
                        console.log('Calendar fares request timed out after 15s');
                    } else {
                        console.error("Calendar fares error:", err);
                    }
                });
        }

        const commonPayloadParams = {
            src: srcPayload,
            dest: destPayload,
            date: effectiveDate,
            nonStop: effectiveNonStop,
            prefTrainClass: selectedSeatClasses.find(c => ['1A', '2A', '3A', 'SL'].includes(c)) || "3A",
            prefFlightClass: selectedSeatClasses.includes('Business') ? 'B' : selectedSeatClasses.includes('First') ? 'F' : 'E',
            numAdults: travelerCounts.adults || 1,
            numChildren: travelerCounts.children || 0,
            numInfants: travelerCounts.infants || 0,
            test: true,
            src_name: appendAirportSuffix(origin, originData),
            dest_name: appendAirportSuffix(destination, destinationData),
            from: fromFieldValue,
            search_session_id: sessionId
        };

        // Sequential message split logic for multi-mode searches with layovers
        // Aim: Distribute CPU intensive mixed-mode calculations across separate lambda instances
        const isFlight = modesToSend.includes('Flight') || modesToSend.includes('All');
        const isTrain = modesToSend.includes('Train') || modesToSend.includes('All');
        const isLayoverSearch = !effectiveNonStop;

        if (isLayoverSearch && isFlight && isTrain) {
            // Split scenario for Flight + Train (Bus is excluded as requested)
            const msg1Modes = ['Train', 'Flight'];
            const msg2Modes = ['MixedTF'];

            if (msg1Modes.length > 0) {
                // Send Msg 1: Primary Modes (will clear results due to 'skipClear: false' in options)
                sendMessage(buildTravelPayload({ ...commonPayloadParams, modes: msg1Modes }), options);

                // Send Msg 2: Mixed Modes after 20ms (must use 'skipClear: true' to append)
                if (msg2Modes.length > 0) {
                    setTimeout(() => {
                        sendMessage(buildTravelPayload({ ...commonPayloadParams, modes: msg2Modes }), { ...options, skipClear: true });
                    }, 20);
                }
                return; // Exit handleSearch after initiating split messages
            }
        }

        // Default: Single message for non-split cases (e.g. non-stop, single mode)
        const payload = buildTravelPayload({ ...commonPayloadParams, modes: modesToSend });
        sendMessage(payload, options);
    };

    // Auto-search on mount + connection open
    useEffect(() => {
        if (connectionStatus === 'open' && !initialSearchTriggered.current) {
            handleSearch();
            initialSearchTriggered.current = true;
        }
    }, [connectionStatus]);

    // Handle manual search button click (from Header or Subheader)
    const onSearchClick = (overrides = {}, options = {}) => {
        handleSearch(overrides, options);
    };

    // Handle Tab/Pill Change (Train/Flight/Bus/Mixed/All)
    const handleModeChange = (newMode) => {
        if (newMode === resultsMode) return;
        setResultsMode(newMode);
    };


    // Generate dates window (7 days centered around viewDate, no past dates)
    // viewDate controls the carousel window; travelDate is the actual selected search date
    // Helper to parse price string to number
    const parsePrice = (priceStr) => {
        if (priceStr === undefined || priceStr === null) return Infinity;
        if (typeof priceStr === 'number') return priceStr;

        // Remove currency symbols, commas, and whitespace
        // Ensure we handle "3,350" -> "3350"
        const cleanStr = priceStr.toString().replace(/,/g, '').replace(/[^0-9.]/g, '');
        const val = parseFloat(cleanStr);
        return isNaN(val) ? Infinity : val;
    };

    // Helper to extract price from route (root or nodes)
    const getRoutePrice = (route) => {
        // 1. Check Root Level
        const rootVal = route.total_fare || route.TotalFare || route.fare || route.Fare || route.price || route.Price || route.amount || route.Amount;
        const rootPrice = parsePrice(rootVal);
        if (rootPrice !== Infinity) return rootPrice;

        // 2. Check Nodes/Segments (Summation or Metadata)
        const nodes = route.nodes || [];
        if (nodes.length > 0) {
            let sum = 0;
            let foundAny = false;

            for (const node of nodes) {
                const seg = node.segment || {};


                // A. Check direct segment fare properties
                let sVal = seg.fare || seg.total_fare || seg.price || seg.Fare || seg.Amount;

                // B. Check seat_costs object if direct price missing
                if (!sVal && seg.seat_costs) {
                    // Try to get price for UI class (e.g. "3A")
                    if (seg.seat_class_ui && seg.seat_costs[seg.seat_class_ui]) {
                        sVal = seg.seat_costs[seg.seat_class_ui];
                    } else {
                        // Fallback: take the lowest non-zero cost
                        const costs = Object.values(seg.seat_costs).map(c => parsePrice(c)).filter(c => c !== Infinity && c > 0);
                        if (costs.length > 0) sVal = Math.min(...costs);
                    }
                }

                // C. Check seat_avail_ui (sometimes has fare/ticket_fare)
                if (!sVal && seg.seat_avail_ui) {
                    sVal = seg.seat_avail_ui.fare || seg.seat_avail_ui.ticket_fare || seg.seat_avail_ui.total_fare;
                }

                const sPrice = parsePrice(sVal);

                if (sPrice !== Infinity) {
                    sum += sPrice;
                    foundAny = true;
                }
            }

            if (foundAny) return sum;
        }

        return Infinity;
    };

    // Pre-calculate cheapest price for the active search date to avoid laggy loops during scroll
    const activeTabCheapestPrice = React.useMemo(() => {
        let routesForPrice = allRoutes;
        if (resultsMode === 'Train') routesForPrice = trainRoutes;
        else if (resultsMode === 'Flight') routesForPrice = flightRoutes;
        else if (resultsMode === 'Bus') routesForPrice = busRoutes;
        else if (resultsMode === 'Mixed') routesForPrice = mixedRoutes;

        if (isLoading && routesForPrice.length === 0) return '-';
        if (routesForPrice.length === 0) return '-';

        let minFare = Infinity;
        routesForPrice.forEach(r => {
            const p = getRoutePrice(r);
            if (p < minFare) minFare = p;
        });

        return minFare !== Infinity ? `₹${Math.round(minFare).toLocaleString('en-IN')}` : '-';
    }, [allRoutes, trainRoutes, flightRoutes, busRoutes, mixedRoutes, resultsMode, isLoading]);

    useEffect(() => {
        if (!viewDate) return;

        const generatedDates = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const currentView = new Date(viewDate);
        currentView.setHours(0, 0, 0, 0);

        // Calculate start date: 3 days before viewDate, but not before today
        let startDate = new Date(currentView);
        startDate.setDate(startDate.getDate() - 3);

        if (startDate < today) {
            startDate = new Date(today);
        }

        // Generate exactly 7 dates from startDate
        for (let i = 0; i < 7; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);

            // Inject pre-calculated price if this date is the current active search date
            const isSelectedSearchDate = travelDate &&
                date.getDate() === travelDate.getDate() &&
                date.getMonth() === travelDate.getMonth() &&
                date.getFullYear() === travelDate.getFullYear();

            // Format date as YYYY-MM-DD for calendarFares lookup
            const yearStr = date.getFullYear();
            const monthStr = String(date.getMonth() + 1).padStart(2, '0');
            const dayStr = String(date.getDate()).padStart(2, '0');
            const dateKey = `${yearStr}-${monthStr}-${dayStr}`;

            // Priority: 1. Active Search Date (from current results), 
            // 2. Calendar API Fares (ONLY when Flight tab is active), 
            // 3. Dash (fallback)
            let priceToDisplay = '-';
            let isPriceLoading = false;

            if (isSelectedSearchDate) {
                priceToDisplay = activeTabCheapestPrice;
                // If it's the active tab but we still have a dash, and main loading is done, 
                // we keep it as '-' but NOT loading.
            } else if (resultsMode === 'Flight') {
                if (calendarFares && calendarFares[dateKey]) {
                    priceToDisplay = `₹${Math.round(calendarFares[dateKey]).toLocaleString('en-IN')}`;
                } else if (isCalendarLoading) {
                    isPriceLoading = true;
                }
            }

            generatedDates.push({
                fullDate: date,
                day: date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }),
                price: priceToDisplay,
                isPriceLoading: isPriceLoading,
                isPast: date < today
            });
        }

        setDates(generatedDates);
    }, [viewDate, travelDate, activeTabCheapestPrice, calendarFares, resultsMode, isCalendarLoading]); // Added isCalendarLoading as dependency


    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // canScrollLeft is based on viewDate (window navigation), not travelDate
    const canScrollLeft = viewDate ? (() => {
        const prev = new Date(viewDate);
        prev.setDate(prev.getDate() - 1);
        prev.setHours(0, 0, 0, 0);
        return prev >= today;
    })() : false;

    // Arrow navigation: only shifts the visible date window, does NOT trigger a search
    const scrollDates = (direction) => {
        if (!viewDate) return;
        const newViewDate = new Date(viewDate);
        if (direction === 'left') {
            newViewDate.setDate(newViewDate.getDate() - 1);
            // Don't go into the past
            if (newViewDate < today) return;
        } else {
            newViewDate.setDate(newViewDate.getDate() + 1);
        }
        setViewDate(newViewDate);
    };

    // Clicking a date card: updates travelDate, syncs viewDate, AND triggers a search
    const handleDateSelect = (date) => {
        setTravelDate(date);
        setViewDate(date);
        if (initialSearchTriggered.current && connectionStatus === 'open') {
            handleSearch({ date });
        }
    };

    // Helper to parse duration string to minutes
    const parseDuration = (durStr) => {
        if (!durStr) return Infinity;

        // 1. Check if pure number string (e.g. "1350")
        if (!isNaN(durStr) && !isNaN(parseFloat(durStr))) {
            return parseFloat(durStr);
        }

        // 2. Check HH:MM format
        if (typeof durStr === 'string' && durStr.includes(':')) {
            const parts = durStr.split(':');
            if (parts.length === 2) {
                const h = parseInt(parts[0]) || 0;
                const m = parseInt(parts[1]) || 0;
                return h * 60 + m;
            }
        }

        // 3. Regex for "22hr 15min" or "2h 30m"
        let totalMinutes = 0;
        const hrMatch = durStr.match(/(\d+)\s*(?:hr|h)/i);
        const minMatch = durStr.match(/(\d+)\s*(?:min|m)/i);

        if (hrMatch) totalMinutes += parseInt(hrMatch[1]) * 60;
        if (minMatch) totalMinutes += parseInt(minMatch[1]);

        if (!hrMatch && !minMatch) return Infinity;

        return totalMinutes || Infinity;
    };

    const extractKms = (comment) => {
        if (!comment) return 0.0;
        const match = String(comment).match(/(\d+(?:\.\d+)?)/);
        return match ? parseFloat(match[1]) : 0.0;
    };

    const AVG_TRAIN_SPEED_KMPH = 50;
    const ACCESS_KMS_THRESHOLD = 100;
    const AVAIL_RATIO_KMS_CUTOFF = 100;

    const getRouteSortMetrics = (route) => {
        const nodes = route.nodes || [];
        const numStops = Math.max(0, nodes.length - 1);
        const duration = getDurationInMinutes(route);

        let availDuration = 0;
        let totalTravelDuration = 0;
        let hasFlight = false;

        const AVG_TRAIN_SPEED_KMPH = 50;
        const AVG_FLIGHT_SPEED_KMPH = 800;

        nodes.forEach(n => {
            const seg = n.segment || {};
            let segDurMins = seg.duration_mins || 30;

            let normalizedDurMins = segDurMins;

            if (seg.flight_num) {
                // 🔹 Floor to nearest 15 mins
                const flooredFlightMins = Math.floor(segDurMins / 15) * 15;

                // Avoid zero edge case (e.g., <15 mins)
                const effectiveFlightMins = Math.max(flooredFlightMins, 15);

                // Step 1: flight duration → distance
                const distanceKm = (effectiveFlightMins / 60) * AVG_FLIGHT_SPEED_KMPH;

                // Step 2: distance → train-equivalent duration
                normalizedDurMins = (distanceKm / AVG_TRAIN_SPEED_KMPH) * 60;

                hasFlight = true;
            }

            totalTravelDuration += normalizedDurMins;

            if (seg.seat_avail_ui?.is_avail) {
                availDuration += normalizedDurMins;
            }
        });
        const firstSeg = nodes[0]?.segment || {};
        const lastSeg = nodes[nodes.length - 1]?.segment || {};

        const originKms = extractKms(firstSeg.origin_comment) || 0;
        const destKms = extractKms(lastSeg.dest_comment) || 0;
        const accessKms = originKms + destKms;

        // --- access duration ---
        const extraDurationMins =
            (accessKms / AVG_TRAIN_SPEED_KMPH) * 60;

        // =========================
        // ✅ AVAILABILITY LOGIC
        // =========================

        const availabilityScore =
            totalTravelDuration > 0
                ? availDuration / totalTravelDuration
                : 0;

        const K = 120;
        const accessPenalty =
            extraDurationMins > 0
                ? extraDurationMins / (K + extraDurationMins)
                : 0;

        const alpha = 0.75;
        const beta = 0.25;

        const weightedScore =
            (alpha * availabilityScore) - (beta * accessPenalty);

        // =========================
        // ✅ EFFECTIVE AVAIL RATIO (clean switch)
        // =========================
        const effectiveAvailRatio =
            accessKms <= AVAIL_RATIO_KMS_CUTOFF
                ? availabilityScore
                : weightedScore;

        // =========================
        // ✅ FINAL AVAIL RATIO (gate on TRUE availability)
        // =========================
        const finalAvailRatio =
            availabilityScore >= 0.60 ? effectiveAvailRatio : 0;

        // =========================
        // ✅ EFFECTIVE STOPS
        // =========================
        const effectiveStops =
            accessKms >= ACCESS_KMS_THRESHOLD
                ? numStops + 1
                : numStops;

        const depTime = firstSeg.origin_dep_dt
            ? new Date(firstSeg.origin_dep_dt).getTime()
            : Infinity;

        const arrTime = lastSeg.dest_reach_dt
            ? new Date(lastSeg.dest_reach_dt).getTime()
            : Infinity;

        return {
            availScore: finalAvailRatio,        // ✅ use this for sorting
            availRatioRaw: availabilityScore,  // debug / analytics
            destKms,
            originKms,
            stops: effectiveStops,
            duration,
            depTime,
            arrTime,
            seg1Avail: !!firstSeg.seat_avail_ui?.is_avail,
            seg2Avail: !!nodes[1]?.segment?.seat_avail_ui?.is_avail,
            totalFare: getRoutePrice(route),
            accessKms
        };
    };

    const getStopsLabel = (count) => {
        if (count === 0) return 'Non-stop';
        if (count === 1) return '1 Stop';
        return `${count} Stops`;
    };


    // Helper to extract duration in minutes from route object
    const getDurationInMinutes = (route) => {
        if (typeof route.duration === 'number') return route.duration;
        if (typeof route.total_duration === 'number') return route.total_duration;

        // Try parsing string
        const durStr = route.duration || route.total_duration;
        if (durStr) {
            const parsed = parseDuration(durStr);
            if (parsed !== Infinity) return parsed;
        }

        // Fallback to nodes calculation
        const nodes = route.nodes || [];
        if (nodes.length > 0) {
            const first = nodes[0].segment;
            const last = nodes[nodes.length - 1].segment;
            if (first?.origin_dep_dt && last?.dest_arr_dt) {
                const start = new Date(first.origin_dep_dt);
                const end = new Date(last.dest_arr_dt);
                const diff = (end - start) / (1000 * 60);
                return diff > 0 ? diff : Infinity;
            }
        }
        return Infinity;
    };

    // Populate Filters based on ALL data to ensure stability across tabs
    useEffect(() => {
        // We use ALL routes to derive the full set of available filters (Operators, Prices, etc.)
        // This ensures filters don't disappear or reset when switching tabs.
        const currentRoutes = allRoutes;

        // Start fresh for collecting available options in the current data set
        const newOperators = {};
        const newSources = {};
        const newDestinations = {};
        const newVia = {};
        const newStops = {};

        const newOperatorPrices = {};
        const newSourceKms = {};
        const newDestinationKms = {};

        let maxD = 0;
        let maxF = 0;
        let maxDepMins = 0;

        if (currentRoutes.length > 0) {
            currentRoutes.forEach(route => {
                // A. Duration Limit Calculation
                const d = getDurationInMinutes(route) / 60;
                if (d !== Infinity && d > maxD) maxD = d;

                // B. Fare Limit Calculation
                const f = getRoutePrice(route);
                if (f !== Infinity && f > maxF) maxF = f;

                // F. Departure Time Limit Calculation
                const depDate = new Date(getRouteSortMetrics(route).depTime);
                const depMins = depDate.getHours() * 60 + depDate.getMinutes();
                if (!isNaN(depMins) && depMins > maxDepMins) maxDepMins = depMins;

                // C. Extract Stops
                const nodes = route.nodes || [];
                const stopsCount = Math.max(0, nodes.length - 1);
                const stopsLabel = getStopsLabel(stopsCount);
                if (newStops[stopsLabel] === undefined) {
                    if (nonStopOnly) {
                        newStops[stopsLabel] = (stopsCount === 0);
                    } else {
                        newStops[stopsLabel] = true;
                    }
                }

                const routePrice = getRoutePrice(route);

                // D. Extract Operators (from all nodes)
                nodes.forEach(node => {
                    let operator = node.segment?.carrier || node.segment?.train_name || node.segment?.travel_name || 'Unknown';
                    const existingKey = Object.keys(newOperators).find(k => k.toLowerCase() === operator.toLowerCase());
                    const keyToUse = existingKey || operator;
                    if (!existingKey) {
                        newOperators[keyToUse] = true;
                    }

                    if (routePrice !== Infinity) {
                        if (newOperatorPrices[keyToUse] === undefined || routePrice < newOperatorPrices[keyToUse]) {
                            newOperatorPrices[keyToUse] = routePrice;
                        }
                    }
                });

                // E. Extract Only Overall Trip Source & Destination
                if (nodes.length > 0) {
                    const firstSeg = nodes[0].segment || {};
                    const lastSeg = nodes[nodes.length - 1].segment || {};

                    const firstSrc = firstSeg.origin_name || firstSeg.origin_code;
                    const lastDest = lastSeg.dest_name || lastSeg.dest_code;

                    if (firstSrc) {
                        const existingSrc = Object.keys(newSources).find(k => k.toLowerCase() === firstSrc.toLowerCase());
                        const keyToUseSrc = existingSrc || firstSrc;
                        if (!existingSrc) {
                            newSources[keyToUseSrc] = true;
                        }
                        const originKmsVal = extractKms(firstSeg.origin_comment);
                        if (originKmsVal > 0) {
                            newSourceKms[keyToUseSrc] = originKmsVal;
                        }
                    }
                    if (lastDest) {
                        const existingDest = Object.keys(newDestinations).find(k => k.toLowerCase() === lastDest.toLowerCase());
                        const keyToUseDest = existingDest || lastDest;
                        if (!existingDest) {
                            newDestinations[keyToUseDest] = true;
                        }
                        const destKmsVal = extractKms(lastSeg.dest_comment);
                        if (destKmsVal > 0) {
                            newDestinationKms[keyToUseDest] = destKmsVal;
                        }
                    }

                    // E. Extract Only Connection Stopover points (VIAs)
                    if (nodes.length > 1) {
                        for (let i = 0; i < nodes.length - 1; i++) {
                            const segArr = nodes[i].segment || {};
                            const segDep = nodes[i + 1].segment || {};

                            const arrName = segArr.arr_at_name || segArr.dest_name;
                            const depName = segDep.dep_from_name || segDep.origin_name;

                            [arrName, depName].forEach(viaName => {
                                if (viaName) {
                                    const existingVia = Object.keys(newVia).find(k => k.toLowerCase() === viaName.toLowerCase());
                                    if (!existingVia) {
                                        newVia[viaName] = true;
                                    }
                                }
                            });
                        }
                    }
                }
            });
        }

        const finalMaxD = currentRoutes.length > 0 ? Math.ceil(maxD) : 0;
        const finalMaxF = currentRoutes.length > 0 ? (Math.ceil(maxF / 1000) * 1000) : 0;
        const finalMaxDep = currentRoutes.length > 0 ? maxDepMins : 0;

        setMaxDurationLimit(finalMaxD);
        setMaxFareLimit(finalMaxF);
        setMaxDepartureLimit(finalMaxDep);

        // Dynamic Expansion Logic: Update user selection to match new data boundaries 
        // if user hasn't narrowed them down yet or if new data exceeds current selection.
        setDurationFilter(prev => {
            if (prev.max === 0 || prev.max >= 999 || prev.max < finalMaxD) {
                return { min: prev.min, max: finalMaxD > 0 ? finalMaxD : 999 };
            }
            return prev;
        });

        setFareFilter(prev => {
            if (prev.max === 0 || prev.max >= 999999 || prev.max < finalMaxF) {
                return { min: prev.min, max: finalMaxF > 0 ? finalMaxF : 999999 };
            }
            return prev;
        });

        setDepartureTimeFilter(prev => {
            if (prev.max === 0 || prev.max >= 1440 || prev.max < finalMaxDep) {
                return { min: prev.min, max: finalMaxDep > 0 ? finalMaxDep : 1440 };
            }
            return prev;
        });

        // Merge Function: Injects new operators from data while PRESERVING user's existing selections
        const mergeFilterState = (prev, newData) => {
            const merged = { ...newData };
            Object.keys(prev).forEach(key => {
                // If the key already exists in user state, keep their preference (checked / unchecked)
                if (merged[key] !== undefined) {
                    merged[key] = prev[key];
                }
            });
            // Update only if there's a structural change or new keys added
            const isSame = Object.keys(prev).length === Object.keys(merged).length &&
                Object.keys(prev).every(k => prev[k] === merged[k]);
            return isSame ? prev : merged;
        };

        setOperatorsFilter(prev => mergeFilterState(prev, newOperators));
        setSourcesFilter(prev => mergeFilterState(prev, newSources));
        setDestinationsFilter(prev => mergeFilterState(prev, newDestinations));
        setStopsFilter(prev => mergeFilterState(prev, newStops));
        setViaFilter(prev => mergeFilterState(prev, newVia));

        setOperatorPrices(prev => JSON.stringify(prev) === JSON.stringify(newOperatorPrices) ? prev : newOperatorPrices);
        setSourceKms(prev => JSON.stringify(prev) === JSON.stringify(newSourceKms) ? prev : newSourceKms);
        setDestinationKms(prev => JSON.stringify(prev) === JSON.stringify(newDestinationKms) ? prev : newDestinationKms);

    }, [allRoutes, trainRoutes, flightRoutes, mixedRoutes, busRoutes]); // Don't include filters in dependency

    // 1. Select basic route set
    const activeRoutes = React.useMemo(() => {
        if (resultsMode === 'Train') return trainRoutes;
        if (resultsMode === 'Flight') return flightRoutes;
        if (resultsMode === 'Mixed') return mixedRoutes;
        if (resultsMode === 'Bus') return busRoutes;
        return allRoutes;
    }, [resultsMode, trainRoutes, flightRoutes, mixedRoutes, busRoutes, allRoutes]);

    // Helper: kya kisi route mein train segment hai?
    const routeHasTrainSegment = (route) => {
        const nodes = route.nodes || [];
        return nodes.some(n => {
            const seg = n.segment || {};
            // Train segment pehchaan: train_num present hai ya flight_num nahi hai
            return seg.train_num || seg.train_name || (!seg.flight_num && !seg.bus_num);
        });
    };

    // "Hide Cancelled Trains" section sirf tab dikhao jab current tab mein train results > 0 hon
    const showHideCancelled = React.useMemo(() => {
        if (resultsMode === 'Train') return trainRoutes.length > 0;
        if (resultsMode === 'Flight') return false;
        if (resultsMode === 'Bus') return false;
        if (resultsMode === 'Mixed') return mixedRoutes.some(routeHasTrainSegment);
        // 'All' tab: train results hain toh dikhao
        return trainRoutes.length > 0;
    }, [resultsMode, trainRoutes, mixedRoutes]);

    const someRouteHasStops = React.useMemo(() => {
        return activeRoutes.some(r => (r.nodes || []).length > 1);
    }, [activeRoutes]);

    // 2. Apply Filters
    let displayedRoutes = activeRoutes.filter(route => {
        // A. Stops
        const nodes = route.nodes || [];
        const stopsCount = Math.max(0, nodes.length - 1);
        const stopsLabel = getStopsLabel(stopsCount);

        if (stopsFilter[stopsLabel] === false) return false;


        // B. Operators (Trains/Airlines)
        // For mixed routes, we show the result if AT LEAST ONE operator is checked.
        // This ensures journeys like "Indigo + Vande Bharat" show when Vande Bharat is selected.
        const anyOperatorChecked = nodes.some(node => {
            const op = node.segment?.carrier || node.segment?.train_name || node.segment?.travel_name || 'Unknown';
            // Match against the deduplicated keys in the filter state
            const opKey = Object.keys(operatorsFilter).find(k => k.toLowerCase() === op.toLowerCase());
            return !opKey || operatorsFilter[opKey] !== false;
        });
        if (!anyOperatorChecked) return false;

        // C. Sources / Destinations
        // Check if the overall trip source/dest matches? Or any segment?
        // Usually filters apply to the Trip Origin and Trip Destination.
        const firstSeg = nodes[0]?.segment || {};
        const lastSeg = nodes[nodes.length - 1]?.segment || {};

        const firstSrcName = firstSeg.origin_name || firstSeg.origin_code;
        const lastDestName = lastSeg.dest_name || lastSeg.dest_code;

        if (firstSrcName) {
            const srcKey = Object.keys(sourcesFilter).find(k => k.toLowerCase() === firstSrcName.toLowerCase());
            if (srcKey && sourcesFilter[srcKey] === false) return false;
        }
        if (lastDestName) {
            const destKey = Object.keys(destinationsFilter).find(k => k.toLowerCase() === lastDestName.toLowerCase());
            if (destKey && destinationsFilter[destKey] === false) return false;
        }

        // F. Via (Stopovers) Check
        if (nodes.length > 1) {
            for (let i = 0; i < nodes.length - 1; i++) {
                const segArr = nodes[i].segment || {};
                const segDep = nodes[i + 1].segment || {};

                const arrName = segArr.arr_at_name || segArr.dest_name;
                const depName = segDep.dep_from_name || segDep.origin_name;

                if (arrName) {
                    const viaKey = Object.keys(viaFilter).find(k => k.toLowerCase() === arrName.toLowerCase());
                    if (viaKey && viaFilter[viaKey] === false) return false;
                }
                if (depName) {
                    const viaKey = Object.keys(viaFilter).find(k => k.toLowerCase() === depName.toLowerCase());
                    if (viaKey && viaFilter[viaKey] === false) return false;
                }
            }
        }

        // D. Duration
        const durMins = getDurationInMinutes(route);
        // Only filter if valid duration is found AND we have a calculated limit AND the user selection is initialized.
        // This prevents results from being hidden while data is still loading.
        if (maxDurationLimit > 0 && durationFilter.max > 0 && durMins !== Infinity) {
            const durHrs = durMins / 60;
            if (durHrs < durationFilter.min || durHrs > durationFilter.max) return false;
        }

        // E. Fare
        const price = getRoutePrice(route);
        // Only filter if valid price is found AND we have a calculated limit AND the user selection is initialized.
        if (maxFareLimit > 0 && fareFilter.max > 0 && price !== Infinity) {
            if (price < fareFilter.min || price > fareFilter.max) return false;
        }

        // G. Departure Time
        const m = getRouteSortMetrics(route);
        if (m.depTime !== Infinity) {
            const depDate = new Date(m.depTime);
            const depTotalMins = depDate.getHours() * 60 + depDate.getMinutes();
            if (depTotalMins < departureTimeFilter.min || depTotalMins > departureTimeFilter.max) return false;
        }

        // F. Hide Cancelled Trains
        if (hideCancelled) {
            const hasCancelled = nodes.some(node => {
                const seg = node.segment || {};
                const seatAvail = seg.seat_avail_ui;

                // Case 1: seat_avail_ui is a string itself (e.g. "Cancelled")
                if (typeof seatAvail === 'string' && seatAvail.toLowerCase().includes('cancelled')) return true;

                // Case 2: seat_avail_ui is an object with an 'avl' property
                if (seatAvail && typeof seatAvail === 'object') {
                    const status = seatAvail.avl || "";
                    if (typeof status === 'string' && status.toLowerCase().includes('cancelled')) return true;
                }

                // Case 3: segment status or other fields (optional but good for robustness)
                const segStatus = seg.status || "";
                if (typeof segStatus === 'string' && segStatus.toLowerCase().includes('cancelled')) return true;

                return false;
            });
            if (hasCancelled) return false;
        }

        // I. Availability Status Filters
        if (showFullyAvailable) {
            let totalSegs = nodes.length;
            let availSegs = 0;

            nodes.forEach(n => {
                const seg = n.segment || {};
                const isFlight = !!seg.flight_num;
                // Flights are treated as available; for others (trains), check seat_avail_ui.is_avail
                if (isFlight || seg.seat_avail_ui?.is_avail) {
                    availSegs++;
                }
            });

            const isFullyAvail = totalSegs > 0 && availSegs === totalSegs;

            if (!isFullyAvail) return false;
        }


        // H. Avoid Station/Airport and Mode Change
        if (avoidStationChange && nodes.length > 1) {
            for (let i = 0; i < nodes.length - 1; i++) {
                const seg1 = nodes[i].segment || {};
                const seg2 = nodes[i + 1].segment || {};

                const arrCode = seg1.arr_at_code || seg1.dest_code;
                const depCode = seg2.dep_from_code || seg2.origin_code;

                // Mode change detection
                const isFlight1 = !!seg1.flight_num;
                const isFlight2 = !!seg2.flight_num;
                const isTrain1 = !!(seg1.train_num || seg1.train_name || (!seg1.flight_num && !seg1.bus_num));
                const isTrain2 = !!(seg2.train_num || seg2.train_name || (!seg2.flight_num && !seg2.bus_num));

                const isModeChange = (isFlight1 !== isFlight2) || (isTrain1 !== isTrain2);

                if ((arrCode && depCode && arrCode !== depCode) || isModeChange) {
                    return false;
                }
            }
        }

        return true;
    });

    // Calculate allowed via options based on selected sources and destinations
    const activeViaOptions = React.useMemo(() => {
        const activeVia = {};

        activeRoutes.forEach(route => {
            const nodes = route.nodes || [];

            // Check Source / Dest only
            const firstSeg = nodes[0]?.segment || {};
            const lastSeg = nodes[nodes.length - 1]?.segment || {};
            const firstSrcName = firstSeg.origin_name || firstSeg.origin_code;
            const lastDestName = lastSeg.dest_name || lastSeg.dest_code;

            if (firstSrcName) {
                const srcKey = Object.keys(sourcesFilter).find(k => k.toLowerCase() === firstSrcName.toLowerCase());
                if (srcKey && sourcesFilter[srcKey] === false) return; // Route doesn't match selected source
            }
            if (lastDestName) {
                const destKey = Object.keys(destinationsFilter).find(k => k.toLowerCase() === lastDestName.toLowerCase());
                if (destKey && destinationsFilter[destKey] === false) return; // Route doesn't match selected dest
            }

            // If it passed Source/Dest filters, its Via options are considered "active"
            if (nodes.length > 1) {
                for (let i = 0; i < nodes.length - 1; i++) {
                    const segArr = nodes[i].segment || {};
                    const segDep = nodes[i + 1].segment || {};
                    const arrName = segArr.arr_at_name || segArr.dest_name;
                    const depName = segDep.dep_from_name || segDep.origin_name;

                    if (arrName) {
                        const viaKey = Object.keys(viaFilter).find(k => k.toLowerCase() === arrName.toLowerCase()) || arrName;
                        activeVia[viaKey] = true;
                    }
                    if (depName) {
                        const viaKey = Object.keys(viaFilter).find(k => k.toLowerCase() === depName.toLowerCase()) || depName;
                        activeVia[viaKey] = true;
                    }
                }
            }
        });

        return activeVia;
    }, [activeRoutes, sourcesFilter, destinationsFilter, viaFilter]);

    // 3. Compute Metrics for Sort Cards (on filtered data)
    const prices = { CheapestAvailable: 0, Best: 0, QuickestAvailable: 0 };
    if (displayedRoutes.length > 0) {
        // We use a "Dry Sort" strategy to find the top route for each category
        const routesCopy = [...displayedRoutes];

        // Best Price
        routesCopy.sort((a, b) => {
            const m1 = getRouteSortMetrics(a);
            const m2 = getRouteSortMetrics(b);

            // Normalize kms: if < 25 → 0, else keep original
            const m1DestAdj = m1.destKms < 25 ? 0 : m1.destKms;
            const m2DestAdj = m2.destKms < 25 ? 0 : m2.destKms;

            const m1OriginAdj = m1.originKms < 25 ? 0 : m1.originKms;
            const m2OriginAdj = m2.originKms < 25 ? 0 : m2.originKms;

            if (m1.availScore !== m2.availScore) return m2.availScore - m1.availScore;
            if (m1.stops !== m2.stops) return m1.stops - m2.stops;
            if (m1.availRatioRaw !== m2.availRatioRaw) return m2.availRatioRaw - m1.availRatioRaw;
            if (m1.seg1Avail !== m2.seg1Avail) return m1.seg1Avail ? -1 : 1;
            if (m1.duration !== m2.duration) return m1.duration - m2.duration;

            // Now compare adjusted kms AFTER duration
            if (m1DestAdj !== m2DestAdj) return m1DestAdj - m2DestAdj;
            if (m1OriginAdj !== m2OriginAdj) return m1OriginAdj - m2OriginAdj;

            return m1.totalFare - m2.totalFare;
        });
        prices.Best = getRoutePrice(routesCopy[0]);

        // Cheapest Available Price
        routesCopy.sort((a, b) => {
            const m1 = getRouteSortMetrics(a);
            const m2 = getRouteSortMetrics(b);
            if (m1.availRatioRaw !== m2.availRatioRaw) return m2.availRatioRaw - m1.availRatioRaw;
            if (m1.totalFare !== m2.totalFare) return m1.totalFare - m2.totalFare;
            return m1.duration - m2.duration;
        });
        prices.CheapestAvailable = getRoutePrice(routesCopy[0]);

        // Quickest Available Price
        routesCopy.sort((a, b) => {
            const m1 = getRouteSortMetrics(a);
            const m2 = getRouteSortMetrics(b);
            if (m1.availRatioRaw !== m2.availRatioRaw) return m2.availRatioRaw - m1.availRatioRaw;
            if (m1.duration !== m2.duration) return m1.duration - m2.duration;
            return m1.totalFare - m2.totalFare;
        });
        prices.QuickestAvailable = getRoutePrice(routesCopy[0]);
    }

    // Update Date Carousel Price for current date based on search results
    useEffect(() => {
        if (!travelDate) return;

        // Format price
        const currentPrice = prices.CheapestAvailable && prices.CheapestAvailable !== Infinity
            ? `₹ ${prices.CheapestAvailable.toLocaleString('en-IN')}`
            : '-';

        setDates(prevDates => {
            const dateStr = travelDate.toDateString();
            const index = prevDates.findIndex(d => d.fullDate.toDateString() === dateStr);

            if (index !== -1 && prevDates[index].price !== currentPrice) {
                const newDates = [...prevDates];
                newDates[index] = { ...newDates[index], price: currentPrice };
                return newDates;
            }
            return prevDates;
        });
    }, [prices.Cheapest, travelDate]); // Only update when price changes or date changes

    // 4. Apply Sorting
    if (displayedRoutes.length > 0) {
        displayedRoutes.sort((a, b) => {
            const m1 = getRouteSortMetrics(a);
            const m2 = getRouteSortMetrics(b);

            if (selectedSort === 'Best') {
                // (-avail_score, stops, -avail_ratio_raw, is_avail, duration, adj_dest_kms, adj_origin_kms, fare)

                const THRESHOLD = 60; // minutes

                // Adjusted kms (existing logic)
                const m1DestAdj = m1.destKms < 25 ? 0 : m1.destKms;
                const m2DestAdj = m2.destKms < 25 ? 0 : m2.destKms;

                const m1OriginAdj = m1.originKms < 50 ? 0 : m1.originKms;
                const m2OriginAdj = m2.originKms < 50 ? 0 : m2.originKms;

                // --- Access distance ---
                const m1AccessKms = (m1.originKms || 0) + (m1.destKms || 0);
                const m2AccessKms = (m2.originKms || 0) + (m2.destKms || 0);

                // --- Effective stops logic ---
                const m1EffectiveStops =
                    (m1.stops === 0 && m1AccessKms >= ACCESS_KMS_THRESHOLD)
                        ? 1
                        : m1.stops;

                const m2EffectiveStops =
                    (m2.stops === 0 && m2AccessKms >= ACCESS_KMS_THRESHOLD)
                        ? 1
                        : m2.stops;

                // --- NEW: Availability ratio gating ---
                const m1EffectiveAvailRatio =
                    m1AccessKms > AVAIL_RATIO_KMS_CUTOFF ? -1 : m1.availRatioRaw;

                const m2EffectiveAvailRatio =
                    m2AccessKms > AVAIL_RATIO_KMS_CUTOFF ? -1 : m2.availRatioRaw;

                // --- SORT ORDER ---

                // 1. Availability score (still primary)
                if (m1.availScore !== m2.availScore) {
                    return m2.availScore - m1.availScore;
                }

                // 2. Stops (conditional logic)
                if (m1EffectiveStops !== m2EffectiveStops) {
                    return m1EffectiveStops - m2EffectiveStops;
                }

                // 3. Availability ratio (gated)
                if (m1EffectiveAvailRatio !== m2EffectiveAvailRatio) {
                    return m2EffectiveAvailRatio - m1EffectiveAvailRatio;
                }

                // 4. First segment availability
                if (m1.seg1Avail !== m2.seg1Avail) {
                    return m1.seg1Avail ? -1 : 1;
                }

                // 5. Destination kms (adjusted)
                if (m1DestAdj !== m2DestAdj) {
                    return m1DestAdj - m2DestAdj;
                }

                // 6. Origin kms (adjusted)
                if (m1OriginAdj !== m2OriginAdj) {
                    return m1OriginAdj - m2OriginAdj;
                }

                // 7. Duration with threshold
                const durationDiff = m1.duration - m2.duration;
                if (Math.abs(durationDiff) >= THRESHOLD) {
                    return durationDiff;
                }

                // 8. Fare fallback
                if (m1.totalFare !== m2.totalFare) {
                    return m1.totalFare - m2.totalFare;
                }

                return 0;
            }
            if (selectedSort === 'Cheapest Available') {
                // (-avail_score, total_fare, -avail_ratio, total_duration)
                if (m1.availRatioRaw !== m2.availRatioRaw) return m2.availRatioRaw - m1.availRatioRaw;
                if (m1.totalFare !== m2.totalFare) return m1.totalFare - m2.totalFare;
                return m1.duration - m2.duration;
            }

            if (selectedSort === 'Quickest Available') {
                // (-avail_score, total_duration, -avail_ratio, total_fare)
                if (m1.availRatioRaw !== m2.availRatioRaw) return m2.availRatioRaw - m1.availRatioRaw;
                if (m1.duration !== m2.duration) return m1.duration - m2.duration;
                return m1.totalFare - m2.totalFare;
            }

            // --- OTHER SORT OPTIONS ---
            if (selectedSort === 'Cheapest') {
                // Normal Cheapest
                return m1.totalFare - m2.totalFare;
            }

            if (selectedSort === 'Quickest') {
                // Normal Quickest
                return m1.duration - m2.duration;
            }

            if (selectedSort === 'Early Arrival') {
                return m1.arrTime - m2.arrTime;
            }

            if (selectedSort === 'Early Departure') {
                return m1.depTime - m2.depTime;
            }

            if (selectedSort === 'Late Departure') {
                return m2.depTime - m1.depTime;
            }

            if (selectedSort === 'Closest Destination') {
                // (dest_kms, -avail_score, origin_kms, num_stops, -avail_ratio_raw, not is_avail, duration)
                if (m1.destKms !== m2.destKms) return m1.destKms - m2.destKms;
                if (m1.availScore !== m2.availScore) return m2.availScore - m1.availScore;
                if (m1.originKms !== m2.originKms) return m1.originKms - m2.originKms;
                if (m1.stops !== m2.stops) return m1.stops - m2.stops;
                if (m1.availRatioRaw !== m2.availRatioRaw) return m2.availRatioRaw - m1.availRatioRaw;
                if (m1.seg1Avail !== m2.seg1Avail) return m1.seg1Avail ? -1 : 1;
                if (m1.duration !== m2.duration) return m1.duration - m2.duration;
                return m1.totalFare - m2.totalFare;
            }

            // Legacy Fallbacks
            if (selectedSort === 'Earliest Available') {
                const aAvail = m1.stops === 0 ? m1.seg1Avail : (m1.seg1Avail && m1.seg2Avail);
                const bAvail = m2.stops === 0 ? m2.seg1Avail : (m2.seg1Avail && m2.seg2Avail);
                if (aAvail !== bAvail) return aAvail ? -1 : 1;
                return m1.depTime - m2.depTime;
            }

            return 0;
        });

        // 5. Identify and Tag Special Routes (Cheapest, Quickest)
        const minPrice = prices.CheapestAvailable;
        let minDuration = Infinity;
        displayedRoutes.forEach(r => {
            const m = getRouteSortMetrics(r);
            if (m.duration < minDuration) minDuration = m.duration;
        });

        // Add tags to the route objects
        displayedRoutes = displayedRoutes.map(route => {
            const rPrice = getRoutePrice(route);
            const m = getRouteSortMetrics(route);
            const tags = [];

            if (rPrice === minPrice && minPrice !== Infinity && minPrice > 0) {
                tags.push('Cheapest');
            }
            if (m.duration === minDuration && minDuration !== Infinity) {
                tags.push('Quickest');
            }
            return { ...route, tags };
        });
    }



    useEffect(() => {
        let timer;
        const potentialEmpty = !isLoading && connectionStatus === 'open' && !error && displayedRoutes.length === 0;

        if (potentialEmpty) {
            setIsWaitingForEmpty(true);
            timer = setTimeout(() => {
                setIsWaitingForEmpty(false);
            }, 1000);
        } else {
            setIsWaitingForEmpty(false);
        }

        return () => clearTimeout(timer);
    }, [isLoading, connectionStatus, error, displayedRoutes.length]);

    // Handle Scroll to Top visibility
    useEffect(() => {
        const handleScroll = () => {
            if (window.scrollY > 400) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Robust UI Logic: Identify which filter options are actually present in the current tab's data.
    // This allows the sidebar to hide irrelevant filters (like "Indigo" in "Train" tab) without resetting global state.
    const modeRelevantKeys = React.useMemo(() => {
        const opKeys = {};
        const srcKeys = {};
        const destKeys = {};
        const viaKeys = {};
        const stopLabels = {};

        activeRoutes.forEach(route => {
            const nodes = route.nodes || [];
            const stopsCount = Math.max(0, nodes.length - 1);
            stopLabels[getStopsLabel(stopsCount)] = true;

            nodes.forEach(n => {
                const seg = n.segment || {};
                const op = seg.carrier || seg.train_name || seg.travel_name || 'Unknown';
                opKeys[op] = true;
            });

            if (nodes.length > 0) {
                const first = nodes[0].segment || {};
                const last = nodes[nodes.length - 1].segment || {};
                const firstSrc = first.origin_name || first.origin_code;
                const lastDest = last.dest_name || last.dest_code;
                if (firstSrc) srcKeys[firstSrc] = true;
                if (lastDest) destKeys[lastDest] = true;

                if (nodes.length > 1) {
                    for (let i = 0; i < nodes.length - 1; i++) {
                        const arr = nodes[i].segment?.arr_at_name || nodes[i].segment?.dest_name;
                        const dep = nodes[i + 1].segment?.dep_from_name || nodes[i + 1].segment?.origin_name;
                        if (arr) viaKeys[arr] = true;
                        if (dep) viaKeys[dep] = true;
                    }
                }
            }
        });

        return { opKeys, srcKeys, destKeys, viaKeys, stopLabels };
    }, [activeRoutes, operatorsFilter, sourcesFilter, destinationsFilter, viaFilter, stopsFilter]);

    // Sidebar should be disabled ONLY if there is genuinely zero actual data from backend for the current mode/tab (and we are not loading)
    const isSidebarDisabled = !isLoading && !headerLoading && activeRoutes.length === 0;

    return (
        <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-900" style={{ backgroundImage: "url('./bgImage.png')", backgroundSize: "cover", backgroundPosition: "center", backgroundRepeat: "no-repeat" }}>

            {/* Header */}
            <SearchHeader
                origin={origin}
                setOrigin={setOrigin}
                originData={originData}
                setOriginData={setOriginData}
                destination={destination}
                setDestination={setDestination}
                destinationData={destinationData}
                setDestinationData={setDestinationData}
                travelDate={travelDate}
                setTravelDate={setTravelDate}
                travelerCounts={travelerCounts}
                setTravelerCounts={setTravelerCounts}
                selectedSeatClasses={selectedSeatClasses}
                setSelectedSeatClasses={setSelectedSeatClasses}
                selectedModes={selectedModes}
                setSelectedModes={setSelectedModes}
                nonStopOnly={nonStopOnly}
                setNonStopOnly={setNonStopOnly}
                handleSearch={onSearchClick}
                isLoading={headerLoading && !isFlightModelError}
            />



            <main className={`max-w-[1440px] mx-auto px-2 md:px-10 py-4 md:py-8 flex ${shouldShowFullScreenStatus ? 'flex-col items-center justify-center min-h-[70vh]' : 'flex-col xl:flex-row'} gap-6 md:gap-8 relative`}>
                {/* Sidebar / Filters Section */}
                {!shouldShowFullScreenStatus && (
                    <div className="flex-shrink-0 w-full xl:w-[280px] hidden xl:block">
                        <div className=" xl:sticky xl:top-[120px] h-auto xl:h-[calc(100vh-120px)] xl:overflow-y-auto scrollbar-hide py-4 pt-0">
                            <FilterSidebar
                                stopsFilter={stopsFilter}
                                setStopsFilter={setStopsFilter}
                                trainsFilter={operatorsFilter}
                                setTrainsFilter={setOperatorsFilter}
                                sourcesFilter={sourcesFilter}
                                setSourcesFilter={setSourcesFilter}
                                destinationsFilter={destinationsFilter}
                                setDestinationsFilter={setDestinationsFilter}
                                operatorPrices={operatorPrices}
                                sourceKms={sourceKms}
                                destinationKms={destinationKms}
                                viaFilter={viaFilter}
                                setViaFilter={setViaFilter}
                                activeOperatorOptions={modeRelevantKeys.opKeys}
                                activeSourceOptions={modeRelevantKeys.srcKeys}
                                activeDestinationOptions={modeRelevantKeys.destKeys}
                                activeStopOptions={modeRelevantKeys.stopLabels}
                                activeViaOptions={modeRelevantKeys.viaKeys}
                                departureTimeFilter={departureTimeFilter}
                                setDepartureTimeFilter={setDepartureTimeFilter}
                                durationFilter={durationFilter}
                                setDurationFilter={setDurationFilter}
                                fareFilter={fareFilter}
                                setFareFilter={setFareFilter}
                                hideCancelled={hideCancelled}
                                setHideCancelled={setHideCancelled}
                                showHideCancelled={showHideCancelled}
                                showFullyAvailable={showFullyAvailable}
                                setShowFullyAvailable={setShowFullyAvailable}
                                avoidStationChange={avoidStationChange}
                                setAvoidStationChange={setAvoidStationChange}
                                showAvoidStationChange={someRouteHasStops}
                                maxDeparture={maxDepartureLimit}
                                maxDuration={maxDurationLimit}
                                maxFare={maxFareLimit}
                                isLoading={isLoading && activeRoutes.length === 0}
                                filteredResultsCount={displayedRoutes.length}
                                isDisabled={isSidebarDisabled}
                                onTabChange={handleModeChange}
                                resultsMode={resultsMode}
                                selectedSort={selectedSort}
                                setSelectedSort={setSelectedSort}
                                showAiFilter={activeSearchMetadata?.use_ai_filter}
                            />
                        </div>
                    </div>
                )}

                {/* Main Content Area */}
                <div className={`flex-1 ${shouldShowFullScreenStatus ? 'w-full max-w-[600px] flex items-center justify-center' : 'max-w-full mx-auto w-full xl:mx-0 xl:max-w-[870px]'} min-w-0`}>
                    {!shouldShowFullScreenStatus && (
                        <div className="pb-4 pt-0 space-y-6 md:space-y-8">

                            <div className="xl:hidden mb-6">
                                <FilterSidebar
                                    stopsFilter={stopsFilter}
                                    setStopsFilter={setStopsFilter}
                                    trainsFilter={operatorsFilter}
                                    setTrainsFilter={setOperatorsFilter}
                                    sourcesFilter={sourcesFilter}
                                    setSourcesFilter={setSourcesFilter}
                                    destinationsFilter={destinationsFilter}
                                    setDestinationsFilter={setDestinationsFilter}
                                    operatorPrices={operatorPrices}
                                    sourceKms={sourceKms}
                                    destinationKms={destinationKms}
                                    viaFilter={viaFilter}
                                    setViaFilter={setViaFilter}
                                    activeOperatorOptions={modeRelevantKeys.opKeys}
                                    activeSourceOptions={modeRelevantKeys.srcKeys}
                                    activeDestinationOptions={modeRelevantKeys.destKeys}
                                    activeStopOptions={modeRelevantKeys.stopLabels}
                                    activeViaOptions={modeRelevantKeys.viaKeys}
                                    departureTimeFilter={departureTimeFilter}
                                    setDepartureTimeFilter={setDepartureTimeFilter}
                                    durationFilter={durationFilter}
                                    setDurationFilter={setDurationFilter}
                                    fareFilter={fareFilter}
                                    setFareFilter={setFareFilter}
                                    hideCancelled={hideCancelled}
                                    setHideCancelled={setHideCancelled}
                                    showHideCancelled={showHideCancelled}
                                    showFullyAvailable={showFullyAvailable}
                                    setShowFullyAvailable={setShowFullyAvailable}
                                    avoidStationChange={avoidStationChange}
                                    setAvoidStationChange={setAvoidStationChange}
                                    showAvoidStationChange={someRouteHasStops}
                                    maxDeparture={maxDepartureLimit}
                                    maxDuration={maxDurationLimit}
                                    maxFare={maxFareLimit}
                                    isLoading={isLoading && activeRoutes.length === 0}
                                    filteredResultsCount={displayedRoutes.length}
                                    isDisabled={isSidebarDisabled}
                                    isMobile={true}
                                    isOpen={showMobileFilters}
                                    onToggle={() => setShowMobileFilters(!showMobileFilters)}
                                    onTabChange={handleModeChange}
                                    resultsMode={resultsMode}
                                    showAiFilter={activeSearchMetadata?.use_ai_filter}
                                />
                            </div>

                            {/* Main Mode Pill */}
                            {/* Main Mode Pill */}
                            <ResultModeSelector
                                resultsMode={resultsMode}
                                setResultsMode={handleModeChange}
                                selectedModes={appliedModes}
                                availability={{
                                    All: allRoutes.length > 0,
                                    Train: trainRoutes.length > 0,
                                    Flight: flightRoutes.length > 0,
                                    // Bus: busRoutes.length > 0,
                                    Mixed: mixedRoutes.length > 0
                                }}
                                nonStopOnly={nonStopOnly}
                                isLoading={isLoading && allRoutes.length === 0}
                            />

                            {/* Date Carousel */}
                            <DateCarousel
                                dates={dates}
                                dateItemsRef={dateItemsRef}
                                scrollDates={scrollDates}
                                selectedDate={travelDate}
                                onDateSelect={handleDateSelect}
                                canScrollLeft={canScrollLeft}
                                isLoading={isLoading && allRoutes.length === 0}
                            />

                            {/* Sort Cards */}
                            <SortCards
                                selectedSort={selectedSort}
                                setSelectedSort={setSelectedSort}
                                prices={prices}
                                isLoading={isLoading && allRoutes.length === 0}
                                isTabLoading={isLoading && displayedRoutes.length === 0}
                            />
                        </div>
                    )}

                    {/* Results Container */}
                    <div className={shouldShowFullScreenStatus ? "w-full flex justify-center" : "space-y-8"}>
                        {shouldShowFullScreenStatus ? (
                            <SearchStatus
                                type={(status === 'error' || isTimeoutError) ? 'no-route' : (status === 'error' ? 'error' : 'no-route')}
                                customTitle={isTimeoutError ? "Search timed out" : (isFlightModelError ? "No routes available for your search" : undefined)}
                                customDescription={isTimeoutError ? "We couldn't receive any data from the server in time. Please try searching again." : (isFlightModelError ? "We couldn't find any travel routes matching your selected locations and dates." : undefined)}
                                showButton={false}
                                isFullScreen={true}
                            />
                        ) : (
                            <>
                                {isOnline && (isLoading || connectionStatus !== 'open') && allRoutes.length === 0 && <SearchLoader />}

                                {isLoading && displayedRoutes.length === 0 && allRoutes.length > 0 && (
                                    <div className="space-y-6">
                                        {[1, 2, 3].map(i => <ResultCardSkeleton key={i} />)}
                                    </div>
                                )}

                                {/* Error handled by Empty State below */}

                                {/* Filter Logic - already computed above */}
                                <>
                                    {(!isLoading && !isWaitingForEmpty && displayedRoutes.length === 0) && (
                                        <SearchStatus
                                            type="no-route"
                                            customTitle={!isOnline ? "Looks like your internet connection is unstable" : `No ${resultsMode !== 'All' ? resultsMode.toLowerCase() : ''} routes found`}
                                            customDescription={!isOnline ? "Please check your network and try again." : "Try adjusting your search filters, dates, or check back later for new routes."}
                                            showButton={false}
                                        />
                                    )}


                                    <TravelResultsList
                                        routes={displayedRoutes}
                                        onBookClick={closeConnection}
                                        onShowError={setGlobalFallbackError}
                                        searchMetadata={activeSearchMetadata}
                                    />
                                </>
                            </>
                        )}
                    </div>
                </div>
            </main >

            {/* Scroll to Top Button */}
            <button
                className={`scroll-to-top-btn ${showScrollTop ? 'visible' : ''}`}
                onClick={scrollToTop}
                aria-label="Scroll to top"
            >
                <ArrowUp size={24} />
            </button>
            <FallbackErrorModal
                isOpen={!!globalFallbackError}
                operationName={globalFallbackError?.operationName || "Booking Error"}
                errorMessage={globalFallbackError?.message || globalFallbackError?.errorMessage}
                onGoBack={() => setGlobalFallbackError(null)}
                backButtonText="Close"
                onContinueWithMock={() => setGlobalFallbackError(null)}
                onTryAgain={() => setGlobalFallbackError(null)}
            />
        </div >
    );
};

export default SearchForm;
